import React, { useCallback, useEffect, useRef, useState } from "react";
import { useLocation, useNavigate, useNavigationType } from "react-router-dom";

const EDGE_PX = 28;
const DECIDE_PX = 10;
const COMMIT_RATIO = 0.28;
const COMMIT_VELOCITY = 0.45;
const AUTH_PATHS = ["/login", "/register", "/forgot-password", "/reset-password"];

/** Session stack — không dùng window.history.length (lẫn lịch sử tab trình duyệt). */
const stack = [];
let stackIndex = -1;

function isAuthPath(pathname = "") {
  return AUTH_PATHS.some((p) => pathname === p || pathname.startsWith(`${p}/`));
}

function isInteractive(el) {
  return Boolean(
    el?.closest?.(
      "input, textarea, select, [contenteditable='true'], [data-swipe-ignore]"
    )
  );
}

function hasBlockingOverlay() {
  const nodes = document.querySelectorAll(".fixed");
  for (const node of nodes) {
    if (node.id === "bottom-nav") continue;
    if (node.dataset.swipeNav === "backdrop") continue;
    const style = window.getComputedStyle(node);
    if (style.display === "none" || style.visibility === "hidden") continue;
    if (Number(style.opacity) === 0) continue;
    const z = Number.parseInt(style.zIndex, 10);
    if (!Number.isFinite(z) || z < 100) continue;
    if (
      node.classList.contains("inset-0") ||
      (style.top === "0px" && style.left === "0px" && style.right === "0px")
    ) {
      return true;
    }
  }
  return false;
}

function syncStack(location, navType) {
  const entry = {
    key: location.key,
    pathname: location.pathname,
  };

  if (stackIndex < 0 || stack.length === 0) {
    stack.length = 0;
    stack.push(entry);
    stackIndex = 0;
    return;
  }

  if (navType === "POP") {
    const found = stack.findIndex((item) => item.key === entry.key);
    if (found >= 0) {
      stackIndex = found;
      return;
    }
  }

  if (navType === "REPLACE") {
    stack[stackIndex] = entry;
    stack.length = stackIndex + 1;
    return;
  }

  if (stack[stackIndex]?.key === entry.key) return;

  stack.length = stackIndex + 1;
  stack.push(entry);
  stackIndex = stack.length - 1;
}

function canGoBack() {
  if (stackIndex <= 0) return false;
  const prev = stack[stackIndex - 1];
  return Boolean(prev && !isAuthPath(prev.pathname));
}

function canGoForward() {
  return stackIndex >= 0 && stackIndex < stack.length - 1;
}

export default function MobileSwipeNavigation({ children, enabled = true }) {
  const location = useLocation();
  const navigate = useNavigate();
  const navType = useNavigationType();
  const layerRef = useRef(null);
  const gestureRef = useRef(null);
  const offsetRef = useRef(0);
  const [offset, setOffset] = useState(0);
  const [dragging, setDragging] = useState(false);
  const [settling, setSettling] = useState(false);

  const applyOffset = useCallback((value) => {
    offsetRef.current = value;
    setOffset(value);
  }, []);

  useEffect(() => {
    syncStack(location, navType);
  }, [location, navType]);

  const resetLayer = useCallback(() => {
    applyOffset(0);
    setDragging(false);
    setSettling(false);
    gestureRef.current = null;
  }, [applyOffset]);

  useEffect(() => {
    resetLayer();
  }, [location.key, resetLayer]);

  useEffect(() => {
    if (!enabled) return undefined;
    const layer = layerRef.current;
    if (!layer) return undefined;

    const onStart = (event) => {
      if (event.touches.length !== 1) return;
      if (hasBlockingOverlay()) return;
      if (isInteractive(event.target)) return;

      const touch = event.touches[0];
      const width = window.innerWidth;
      const fromLeft = touch.clientX <= EDGE_PX;
      const fromRight = touch.clientX >= width - EDGE_PX;
      if (!fromLeft && !fromRight) return;

      const dir = fromLeft ? "back" : "forward";
      if (dir === "back" && !canGoBack()) return;
      if (dir === "forward" && !canGoForward()) return;

      gestureRef.current = {
        dir,
        startX: touch.clientX,
        startY: touch.clientY,
        startAt: event.timeStamp,
        lastX: touch.clientX,
        lastAt: event.timeStamp,
        decided: false,
        active: false,
      };
    };

    const onMove = (event) => {
      const g = gestureRef.current;
      if (!g || event.touches.length !== 1) return;

      const touch = event.touches[0];
      const dx = touch.clientX - g.startX;
      const dy = touch.clientY - g.startY;
      g.lastX = touch.clientX;
      g.lastAt = event.timeStamp;

      if (!g.decided) {
        if (Math.abs(dx) < DECIDE_PX && Math.abs(dy) < DECIDE_PX) return;
        g.decided = true;
        const horizontal = Math.abs(dx) > Math.abs(dy);
        const correctWay = g.dir === "back" ? dx > 0 : dx < 0;
        if (!horizontal || !correctWay) {
          gestureRef.current = null;
          return;
        }
        g.active = true;
        setDragging(true);
      }

      if (!g.active) return;
      event.preventDefault();

      const width = window.innerWidth;
      const raw = g.dir === "back" ? Math.max(0, dx) : Math.min(0, dx);
      const next = Math.max(-width, Math.min(width, raw));
      applyOffset(next);
    };

    const onEnd = (event) => {
      const g = gestureRef.current;
      if (!g) return;

      if (!g.active) {
        gestureRef.current = null;
        return;
      }

      const width = window.innerWidth;
      const elapsed = Math.max(1, event.timeStamp - g.startAt);
      const dx = g.lastX - g.startX;
      const velocity = dx / elapsed;
      const progress = Math.abs(offsetRef.current || dx) / width;
      const commit =
        progress >= COMMIT_RATIO ||
        (g.dir === "back" && velocity > COMMIT_VELOCITY) ||
        (g.dir === "forward" && velocity < -COMMIT_VELOCITY);

      if (!commit) {
        setSettling(true);
        applyOffset(0);
        window.setTimeout(resetLayer, 220);
        return;
      }

      setSettling(true);
      applyOffset(g.dir === "back" ? width : -width);
      window.setTimeout(() => {
        navigate(g.dir === "back" ? -1 : 1);
        resetLayer();
      }, 180);
    };

    layer.addEventListener("touchstart", onStart, { passive: true, capture: true });
    layer.addEventListener("touchmove", onMove, { passive: false, capture: true });
    layer.addEventListener("touchend", onEnd, { capture: true });
    layer.addEventListener("touchcancel", resetLayer, { capture: true });

    return () => {
      layer.removeEventListener("touchstart", onStart, { capture: true });
      layer.removeEventListener("touchmove", onMove, { capture: true });
      layer.removeEventListener("touchend", onEnd, { capture: true });
      layer.removeEventListener("touchcancel", resetLayer, { capture: true });
    };
  }, [applyOffset, enabled, navigate, resetLayer]);

  const width = typeof window !== "undefined" ? window.innerWidth : 1;
  const progress = Math.min(1, Math.abs(offset) / width);
  const goingBack = offset > 0;

  return (
    <div className="relative z-0 flex min-h-0 min-w-0 flex-1 flex-col overflow-hidden">
      <div
        data-swipe-nav="backdrop"
        className="pointer-events-none absolute inset-0 bg-slate-900/25"
        style={{ opacity: dragging || settling ? progress * 0.9 : 0 }}
        aria-hidden
      />
      {(dragging || settling) && progress > 0 ? (
        <div
          className="pointer-events-none absolute inset-y-0 flex w-16 items-center justify-center text-white/80"
          style={{
            left: goingBack ? 8 : "auto",
            right: goingBack ? "auto" : 8,
            opacity: Math.min(1, progress * 1.4),
          }}
          aria-hidden
        >
          <span className="rounded-full bg-black/25 px-2 py-1 text-[10px] font-semibold uppercase tracking-wide">
            {goingBack ? "Quay lại" : "Tiếp tục"}
          </span>
        </div>
      ) : null}

      <div
        ref={layerRef}
        className={`relative z-0 flex min-h-0 flex-1 flex-col ${
          dragging || settling ? "will-change-transform" : ""
        }`}
        style={{
          transform: offset ? `translate3d(${offset}px,0,0)` : "none",
          transition:
            dragging && !settling
              ? "none"
              : "transform 180ms cubic-bezier(0.2, 0.8, 0.2, 1)",
          boxShadow:
            dragging || settling
              ? "-12px 0 28px rgba(15, 23, 42, 0.18)"
              : "none",
        }}
      >
        {children}
      </div>
    </div>
  );
}
